import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translateGraphQLError} from '../../../../utils/graphqlUtils';
import type {AutoAnimationPlugin} from '@formkit/auto-animate';
import {useAutoAnimate} from '@formkit/auto-animate/react';
import {Button, Checkbox, Drawer, Input, Modal, notification, Select, Switch, Typography} from 'antd';
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    EditOutlined,
    EnvironmentOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import {GoogleMap, useJsApiLoader, Polygon, Marker} from '@react-google-maps/api';
import {useMutation, useQuery} from '@apollo/client';
import {
    SAVE_ROUTE_PLANNING_TEMPLATE,
    type SaveRoutePlanningTemplateResponse,
    type SaveRoutePlanningTemplateVariables,
} from '../graphql/template/saveRoutePlanningTemplate';
import {
    LIST_ROUTE_PLANNING_TEMPLATES,
    type ListRoutePlanningTemplatesResponse,
    type ListRoutePlanningTemplatesVariables,
} from '../graphql/template/listRoutePlanningTemplates';
import {
    SET_DEFAULT_ROUTE_PLANNING_TEMPLATE,
    type SetDefaultRoutePlanningTemplateResponse,
    type SetDefaultRoutePlanningTemplateVariables,
} from '../graphql/template/setDefaultRoutePlanningTemplate';
import {
    DELETE_ROUTE_PLANNING_TEMPLATE,
    type DeleteRoutePlanningTemplateResponse,
    type DeleteRoutePlanningTemplateVariables,
} from '../graphql/template/deleteRoutePlanningTemplate';
import {getAssignTypeLabel} from '../utils/assignTypeLabel';
import {type SplitRouteRecord} from './SplitRouteDrawer';
import {type DriverType} from '../graphql/deliveryRoutes';
import {type SplitMode} from '../utils/splitModeConfig';
import {AssignType, type DriverAddressInput} from '../graphql/assignDrivers';
import AddressInputModal, {type AddressResult} from './AddressInputModal';
import {GET_WAREHOUSE_BY_ID, type GetWarehouseByIdResponse} from '../../../../graphql/queries/warehouseQueries';
import {
    GET_ZIPCODE_MAP_BOUNDARIES_BY_AREA_ID,
    type GetZipcodeMapBoundariesByAreaIdResponse,
    type GetZipcodeMapBoundariesByAreaIdVariables,
} from '../graphql/getZipcodeMapBoundariesByAreaId';
import {
    GET_AREA_BY_ID,
    type GetAreaByIdResponse,
    type GetAreaByIdVariables,
} from '../graphql/getAreaById';
import { resolveWarehouseNow } from '../utils/taskDateRule';
import {
    GET_WAREHOUSE_PLANNING_ENTRY_CONFIG_BY_WAREHOUSE_ID,
    type GetWarehousePlanningEntryConfigByWarehouseIdResponse,
    type GetWarehousePlanningEntryConfigByWarehouseIdVariables,
} from '../graphql/getWarehousePlanningEntryConfigByWarehouseId';
import {
    GET_VOLUME_FORECAST,
    type GetVolumeForecastResponse,
    type GetVolumeForecastVariables,
} from '../graphql/template/getVolumeForecast';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {getCurrentTimeInTimezone} from '../../../../utils/timezoneUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

const {Text} = Typography;

const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';
const defaultCenter = {lat: 34.0522, lng: -118.2437};

interface RoutePlanningTemplateProps {
    visible: boolean;
    record: SplitRouteRecord | null;
    warehouseId: number | undefined;
    driversByDspId: Record<number, DriverType[]>;
    allowedModes?: SplitMode[];
    onClose: () => void;
}

type ViewMode = 'list' | 'editing';

interface TemplateSegment {
    id: string;
    detailId: number | null;
    name: string;
    colorIndex: number;
    driverAccountId: string;
    driverName: string;
    capacityMin: number | null;
    capacityMax: number | null;
    preferredZipcodes: string[];
    startAddress: DriverAddressInput | null;
    endAddress: DriverAddressInput | null;
}

const SEGMENT_ACCENT_COLORS = [
    '#4F46E5', '#0EA5E9', '#F59E0B', '#10B981', '#EF4444',
    '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
];

const formatAddress = (addr?: DriverAddressInput | null): string => {
    if (!addr) return '';
    return [addr.streetAddress, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ');
};

const createEmptySegment = (index: number, areaName: string): TemplateSegment => ({
    id: `seg-${Date.now()}-${index}`,
    detailId: null,
    name: `${areaName}-${index + 1}`,
    colorIndex: index % SEGMENT_ACCENT_COLORS.length,
    driverAccountId: '',
    driverName: '',
    capacityMin: null,
    capacityMax: null,
    preferredZipcodes: [],
    startAddress: null,
    endAddress: null,
});

const RoutePlanningTemplate: React.FC<RoutePlanningTemplateProps> = ({
    visible,
    record,
    warehouseId,
    driversByDspId,
    onClose,
}) => {
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
    const [showZipcodeBoundary, setShowZipcodeBoundary] = useState(true);
    const [hideMapLabels, setHideMapLabels] = useState(true);
    const [templateName, setTemplateName] = useState('');
    const [segments, setSegments] = useState<TemplateSegment[]>([]);

    const [addressModalTarget, setAddressModalTarget] = useState<{segId: string; field: 'start' | 'end'} | null>(null);
    const [pinningTarget, setPinningTarget] = useState<{segId: string; field: 'start' | 'end'} | null>(null);
    const [showAddress, setShowAddress] = useState(true);
    const [saving, setSaving] = useState(false);
    // 保存时命中的配送能力校验提示（非空即弹窗展示，含"仍要提交/返回编辑"）
    const [capacityWarnings, setCapacityWarnings] = useState<string[] | null>(null);
    // 各分段配送能力输入框的失焦校验错误文案（按 seg.id），在输入框下方红字展示，重新输入时清除
    const [capacityErrors, setCapacityErrors] = useState<Record<string, string>>({});
    const [panExpanded, setPanExpanded] = useState(false);
    const {t} = useTranslation();
    // 设为默认后列表会按"默认置顶"重排，用 auto-animate 给换位加滑动过渡，避免视觉误差。
    // 自定义动画：换位只取 Y 方向位移（丢弃 X），避免因滚动条出现/布局 reflow 产生的横向滑动
    const templateListAnimatePlugin: AutoAnimationPlugin = (el, action, newCoords, oldCoords) => {
        const duration = 250;
        let keyframes: Keyframe[];
        if (action === 'remain' && oldCoords && newCoords) {
            const deltaY = oldCoords.top - newCoords.top;
            keyframes = [
                {transform: `translateY(${deltaY}px)`},
                {transform: 'translateY(0)'},
            ];
        } else if (action === 'add') {
            keyframes = [
                {opacity: 0, transform: 'translateY(-8px)'},
                {opacity: 1, transform: 'translateY(0)'},
            ];
        } else {
            keyframes = [
                {opacity: 1},
                {opacity: 0},
            ];
        }
        return new KeyframeEffect(el, keyframes, {duration, easing: 'ease-in-out'});
    };
    const [templateListRef] = useAutoAnimate<HTMLDivElement>(templateListAnimatePlugin);
    const mapRef = useRef<google.maps.Map | null>(null);
    const hasFittedBounds = useRef(false);

    useEffect(() => {
        if (!visible) {
            setViewMode('list');
            setEditingTemplateId(null);
            setTemplateName('');
            setSegments([]);
            setAddressModalTarget(null);
            setPinningTarget(null);
            setSaving(false);
            setCapacityWarnings(null);
            setCapacityErrors({});
            setPanExpanded(false);
            hasFittedBounds.current = false;
        }
    }, [visible]);

    const {data: templateListData, refetch: refetchTemplates} = useQuery<
        ListRoutePlanningTemplatesResponse,
        ListRoutePlanningTemplatesVariables
    >(LIST_ROUTE_PLANNING_TEMPLATES, {
        variables: {areaId: record?.areaId ?? 0, warehouseId: warehouseId ?? 0},
        skip: !visible || !record?.areaId || !warehouseId,
    });

    const templateList = templateListData?.listRoutePlanningTemplates ?? [];
    const defaultTemplate = templateList.find(t => t.isDefault) ?? null;

    const {data: warehouseData} = useQuery<GetWarehouseByIdResponse>(
        GET_WAREHOUSE_BY_ID,
        {
            variables: {id: warehouseId},
            skip: !visible || !warehouseId,
        }
    );

    // 预测派送量按仓库所在时区的当地当前日期查询（后端再查次日），不取前端选择的任务日期
    const forecastDate = warehouseData?.warehouse?.timezoneLocation
        ? getCurrentTimeInTimezone(warehouseData.warehouse.timezoneLocation).format('YYYY-MM-DD')
        : null;

    const {data: forecastData} = useQuery<GetVolumeForecastResponse, GetVolumeForecastVariables>(
        GET_VOLUME_FORECAST,
        {
            variables: {warehouseId: warehouseId ?? 0, areaId: record?.areaId ?? 0, forecastDate: forecastDate ?? ''},
            skip: !visible || !warehouseId || !record?.areaId || !forecastDate,
        }
    );
    const forecastParcelCount = forecastData?.getVolumeForecast ?? null;

    const {data: entryConfigData} = useQuery<
        GetWarehousePlanningEntryConfigByWarehouseIdResponse,
        GetWarehousePlanningEntryConfigByWarehouseIdVariables
    >(GET_WAREHOUSE_PLANNING_ENTRY_CONFIG_BY_WAREHOUSE_ID, {
        variables: {warehouseId: warehouseId ?? 0},
        skip: !visible || !warehouseId,
    });
    const entryConfig = entryConfigData?.config ?? null;
    const editStartTime = entryConfig?.templateEditStartTime?.substring(0, 5) ?? null;
    const editEndTime = entryConfig?.templateEditEndTime?.substring(0, 5) ?? null;

    const [saveTemplateMutation] = useMutation<
        SaveRoutePlanningTemplateResponse,
        SaveRoutePlanningTemplateVariables
    >(SAVE_ROUTE_PLANNING_TEMPLATE);

    const [setDefaultMutation] = useMutation<
        SetDefaultRoutePlanningTemplateResponse,
        SetDefaultRoutePlanningTemplateVariables
    >(SET_DEFAULT_ROUTE_PLANNING_TEMPLATE);

    const [deleteTemplateMutation] = useMutation<
        DeleteRoutePlanningTemplateResponse,
        DeleteRoutePlanningTemplateVariables
    >(DELETE_ROUTE_PLANNING_TEMPLATE);

    const handleAddressConfirm = (result: AddressResult) => {
        if (!addressModalTarget) return;
        const {segId, field} = addressModalTarget;
        const addr: DriverAddressInput = {
            streetAddress: result.streetAddress ?? null,
            unit: result.unit ?? null,
            city: result.city ?? null,
            state: result.state ?? null,
            zipCode: result.zipCode ?? null,
            county: result.county ?? null,
            latitude: result.latitude ?? null,
            longitude: result.longitude ?? null,
        };
        const patch = field === 'start' ? {startAddress: addr} : {endAddress: addr};
        updateSegment(segId, patch);
        setAddressModalTarget(null);
    };

    const updateSegment = (id: string, patch: Partial<TemplateSegment>) => {
        setSegments(prev => prev.map(s => s.id === id ? {...s, ...patch} : s));
    };

    const driverOptions = useMemo(() => {
        const drivers = driversByDspId[record?.dspId ?? 0] ?? [];
        return drivers
            .filter((d: DriverType) => d.isEmployed)
            .map((d: DriverType) => {
                const isVerified = d.verificationStatus === 2;
                const isBlacklisted = !!d.isBlacklist;
                return {
                    label: (
                        <span>
                            <span style={{fontSize: 14}}>{d.phone}</span>
                            <span style={{fontSize: 12, color: '#666', marginLeft: 4}}>({d.name})</span>
                            {isBlacklisted ? (
                                <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('splitOrder.template.blacklisted')}</span>
                            ) : isVerified ? (
                                <span style={{fontSize: 12, color: '#52c41a', marginLeft: 8}}>{t('splitOrder.template.verified')}</span>
                            ) : (
                                <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('splitOrder.template.unverified')}</span>
                            )}
                        </span>
                    ),
                    value: d.accountId.toString(),
                    disabled: isBlacklisted || !isVerified,
                    name: d.name,
                    phone: d.phone,
                    driverLicenseNumber: d.driverLicenseNumber,
                };
            });
    }, [driversByDspId, record?.dspId]);

    const driverFilterOption = (input: string, option: any) => {
        const kw = input.trim().toLowerCase();
        if (!kw) return true;
        return String(option?.phone ?? '').toLowerCase().includes(kw) ||
            String(option?.name ?? '').toLowerCase().includes(kw) ||
            String(option?.driverLicenseNumber ?? '').toLowerCase().includes(kw);
    };

    const isTemplateEditable = useMemo(() => {
        if (!editStartTime || !editEndTime) return true;
        // 优先用服务器按仓库时区换算的当前时间（防设备时钟篡改）；缺失时降级为设备时间
        const whsNow = resolveWarehouseNow(entryConfig?.warehouseCurrentTime);
        const localMinutes = whsNow.hour() * 60 + whsNow.minute();
        const [sh, sm] = editStartTime.split(':').map(Number);
        const [eh, em] = editEndTime.split(':').map(Number);
        const startMinutes = sh * 60 + sm;
        const endMinutes = eh * 60 + em;
        return localMinutes >= startMinutes && localMinutes < endMinutes;
    }, [editStartTime, editEndTime, entryConfig?.warehouseCurrentTime]);

    const whsDriverAddress = useMemo((): DriverAddressInput | null => {
        const w = warehouseData?.warehouse;
        if (!w) return null;
        return {
            streetAddress: w.addressLine1 ?? null,
            city: w.city ?? null,
            state: w.state ?? null,
            zipCode: w.zipcode ?? null,
            latitude: w.location?.latitude ?? null,
            longitude: w.location?.longitude ?? null,
        };
    }, [warehouseData]);

    const whsLat = warehouseData?.warehouse?.location?.latitude ?? undefined;
    const whsLng = warehouseData?.warehouse?.location?.longitude ?? undefined;

    const isWhsAddress = useCallback((addr: DriverAddressInput | null | undefined): boolean => {
        if (!whsDriverAddress || !addr) return false;
        if (addr.latitude != null && whsLat != null) {
            return addr.latitude === whsLat && addr.longitude === whsLng;
        }
        return addr.streetAddress === whsDriverAddress.streetAddress &&
            addr.city === whsDriverAddress.city &&
            addr.state === whsDriverAddress.state &&
            addr.zipCode === whsDriverAddress.zipCode;
    }, [whsDriverAddress, whsLat, whsLng]);

    // 计算保存时的配送能力软校验提示（命中则弹窗，可"仍要提交"）
    const computeCapacityWarnings = (): string[] => {
        const warnings: string[] = [];
        // 场景1：上限与下限太接近 (上限-下限)/上限 < 15%
        segments.forEach(s => {
            if (!s.capacityMax || s.capacityMax <= 0 || s.capacityMin == null) return;
            if ((s.capacityMax - s.capacityMin) / s.capacityMax < 0.15) {
                warnings.push(t('splitOrder.template.capacityTooCloseWarn', {name: s.driverName || s.name}));
            }
        });
        // 场景2/3：需预估单量；取不到则跳过（未填的上限/下限按 0 计入求和）
        if (forecastParcelCount != null) {
            const totalMax = segments.reduce((sum, s) => sum + (s.capacityMax ?? 0), 0);
            const totalMin = segments.reduce((sum, s) => sum + (s.capacityMin ?? 0), 0);
            if (totalMax < forecastParcelCount) {
                warnings.push(t('splitOrder.template.totalMaxInsufficientWarn', {x: forecastParcelCount, y: totalMax}));
            }
            if (totalMin > forecastParcelCount) {
                warnings.push(t('splitOrder.template.totalMinExceedsWarn', {x: forecastParcelCount, y: totalMin}));
            }
        }
        return warnings;
    };

    const handleSaveTemplate = () => {
        const errors: string[] = [];
        if (!templateName.trim()) {
            errors.push(t('splitOrder.template.templateNameRequired'));
        }
        const noDriver = segments.filter(s => !s.driverAccountId);
        if (noDriver.length > 0) {
            errors.push(t('splitOrder.template.noDriverAssigned', {segments: noDriver.map(s => s.name).join(', ')}));
        }
        const noCapacity = segments.filter(s => s.capacityMin == null || s.capacityMax == null);
        if (noCapacity.length > 0) {
            errors.push(t('splitOrder.template.capacityRequired', {segments: noCapacity.map(s => s.name).join(', ')}));
        }
        if (errors.length > 0) {
            notification.warning({message: errors[0], duration: 3});
            return;
        }
        // 三场景软校验：命中则弹窗让用户选择"仍要提交/返回编辑"，否则直接保存
        const warnings = computeCapacityWarnings();
        if (warnings.length > 0) {
            setCapacityWarnings(warnings);
            return;
        }
        doSaveTemplate();
    };

    const doSaveTemplate = async () => {
        setSaving(true);
        try {
            const isFirstTemplate = templateList.length === 0 && editingTemplateId == null;
            const areaId = record?.areaId ?? 0;
            const dspId = record?.dspId ?? 0;
            const whId = warehouseId ?? 0;
            const {data: saveData} = await saveTemplateMutation({
                variables: {
                    input: {
                        id: editingTemplateId,
                        name: templateName.trim(),
                        areaId,
                        dspId,
                        warehouseId: whId,
                        assignType: AssignType.BY_ALGORITHM,
                        details: segments.map(seg => ({
                            id: seg.detailId ?? null,
                            driverAccountId: Number(seg.driverAccountId),
                            driverName: seg.driverName || null,
                            capacityMin: seg.capacityMin,
                            capacityMax: seg.capacityMax,
                            preferredZipcodes: seg.preferredZipcodes,
                            startAddress: seg.startAddress ? {
                                streetAddress: seg.startAddress.streetAddress,
                                city: seg.startAddress.city,
                                state: seg.startAddress.state,
                                zipCode: seg.startAddress.zipCode,
                                latitude: seg.startAddress.latitude,
                                longitude: seg.startAddress.longitude,
                            } : null,
                            endAddress: seg.endAddress ? {
                                streetAddress: seg.endAddress.streetAddress,
                                city: seg.endAddress.city,
                                state: seg.endAddress.state,
                                zipCode: seg.endAddress.zipCode,
                                latitude: seg.endAddress.latitude,
                                longitude: seg.endAddress.longitude,
                            } : null,
                        })),
                    },
                },
            });
            if (isFirstTemplate && saveData?.saveRoutePlanningTemplate?.id) {
                await setDefaultMutation({
                    variables: {
                        id: saveData.saveRoutePlanningTemplate.id,
                        areaId,
                        warehouseId: whId,
                    },
                });
            }
            notification.success({message: t('splitOrder.template.saveSuccess'), duration: 2});
            await refetchTemplates();
            setSegments([]);
            setTemplateName('');
            setEditingTemplateId(null);
            setViewMode('list');
        } catch (e: any) {
            notification.error({message: translateGraphQLError(e, 'splitOrder.template.saveFailed'), duration: 3});
        } finally {
            setSaving(false);
        }
    };

    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!pinningTarget || !e.latLng) return;
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const target = pinningTarget;
        setPinningTarget(null);
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({location: {lat, lng}}, (results, status) => {
            let driverAddr: DriverAddressInput | null = null;
            if (status === 'OK' && results && results.length > 0) {
                let bestStreetAddress = '';
                let bestCity = '';
                let bestState = '';
                let bestZipCode = '';
                let bestCounty = '';
                for (const r of results) {
                    const get = (type: string) =>
                        r.address_components.find((c: any) => c.types.includes(type))?.long_name ?? '';
                    const getShort = (type: string) =>
                        r.address_components.find((c: any) => c.types.includes(type))?.short_name ?? '';
                    const streetNumber = get('street_number');
                    const route = get('route');
                    const city = get('locality') || get('sublocality');
                    const state = getShort('administrative_area_level_1');
                    const zipCode = get('postal_code');
                    const county = get('administrative_area_level_2');
                    const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
                    if (streetAddress && !bestStreetAddress) bestStreetAddress = streetAddress;
                    if (city && !bestCity) bestCity = city;
                    if (state && !bestState) bestState = state;
                    if (zipCode && !bestZipCode) bestZipCode = zipCode;
                    if (county && !bestCounty) bestCounty = county;
                }
                if (bestCity || bestStreetAddress) {
                    driverAddr = {
                        streetAddress: bestStreetAddress || null,
                        city: bestCity || null,
                        state: bestState || null,
                        zipCode: bestZipCode || null,
                        county: bestCounty || null,
                        latitude: lat,
                        longitude: lng,
                    };
                }
            }
            if (!driverAddr) {
                notification.error({
                    message: t('splitOrder.template.getAddressFailed'),
                    description: t('splitOrder.template.invalidAddress'),
                    placement: 'topLeft',
                    duration: 3,
                });
                return;
            }
            const updates = target.field === 'start'
                ? {startAddress: driverAddr}
                : {endAddress: driverAddr};
            updateSegment(target.segId, updates);
        });
    }, [pinningTarget, updateSegment]);

    const {isLoaded} = useJsApiLoader({id: 'google-map-script', googleMapsApiKey: GOOGLE_MAPS_API_KEY});

    const { data: areaData } = useQuery<GetAreaByIdResponse, GetAreaByIdVariables>(
        GET_AREA_BY_ID,
        {
            variables: { areaId: record?.areaId! },
            skip: !visible || !record?.areaId,
            fetchPolicy: 'cache-and-network',
        }
    );

    const { data: zipcodeBoundariesData } = useQuery<GetZipcodeMapBoundariesByAreaIdResponse, GetZipcodeMapBoundariesByAreaIdVariables>(
        GET_ZIPCODE_MAP_BOUNDARIES_BY_AREA_ID,
        {
            variables: { areaId: record?.areaId! },
            skip: !visible || !record?.areaId,
            fetchPolicy: 'cache-and-network',
        }
    );

    const zipcodeBoundaryData = useMemo(() => {
        const items = zipcodeBoundariesData?.getZipcodeMapBoundariesByAreaId;
        if (!items?.length) return [];
        const entries: { zipcode: string; paths: google.maps.LatLngLiteral[] }[] = [];
        for (const item of items) {
            try {
                const coords = JSON.parse(item.boundary);
                const rawPath = Array.isArray(coords?.[0]) ? coords[0] : coords;
                const path = (rawPath as any[])
                    .filter((p: any) => typeof p.lat === 'number' && typeof p.lng === 'number')
                    .map((p: any) => ({ lat: p.lat, lng: p.lng }));
                if (path.length === 0) continue;
                entries.push({ zipcode: item.zipcode, paths: path });
            } catch {
                // 解析失败静默跳过
            }
        }
        return entries;
    }, [zipcodeBoundariesData]);

    const zipcodeOptions = useMemo(() => {
        const zipcodes = areaData?.getAreaById?.zipcodeList ?? [];
        return zipcodes
            .slice().sort()
            .map(zip => ({label: zip, value: zip}));
    }, [areaData]);

    const mapCenter = useMemo(() => {
        if (whsLat != null && whsLng != null) return {lat: whsLat, lng: whsLng};
        return defaultCenter;
    }, [whsLat, whsLng]);

    useEffect(() => {
        if (!visible) {
            hasFittedBounds.current = false;
        }
    }, [visible]);

    useEffect(() => {
        if (hasFittedBounds.current || !mapRef.current || zipcodeBoundaryData.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        if (whsLat != null && whsLng != null) {
            bounds.extend({lat: whsLat, lng: whsLng});
        }
        for (const entry of zipcodeBoundaryData) {
            for (const pt of entry.paths) {
                bounds.extend(pt);
            }
        }
        mapRef.current.fitBounds(bounds, 40);
        hasFittedBounds.current = true;
    }, [zipcodeBoundaryData, whsLat, whsLng]);

    const areaName = record?.areaName ?? '-';

    return (
        <Drawer
            open={visible}
            onClose={onClose}
            placement="right"
            width="100vw"
            closable={false}
            bodyStyle={{padding: 0, display: 'flex', flexDirection: 'column', height: '100%'}}
        >
            {/* ===== 顶部信息栏 ===== */}
            <div className="flex border-b border-gray-200 bg-white shrink-0 py-3">
                {/* 左侧：返回 + 标题（与下方左侧面板等宽） */}
                <div className="shrink-0 flex items-center gap-3" style={{width: 420}}>
                    <Button
                        type="text"
                        onClick={() => {onClose();}}
                        style={{color: '#7c3aed', padding: '0 4px'}}
                        icon={
                            <ArrowLeftOutlined style={{
                                fontSize: 22,
                                color: '#7c3aed',
                                filter: 'drop-shadow(0 0 0.5px #7c3aed) drop-shadow(0 0 0.5px #7c3aed)',
                            }} />
                        }
                    />
                    <div className="border-l-4 border-[#7c3aed] pl-2">
                        <Text className="text-xl font-bold">
                            {t('splitOrder.template.routeTemplate', {name: areaName})}
                        </Text>
                    </div>
                </div>

                {/* 右侧：统计 + 操作按钮 */}
                <div className="flex-1 flex flex-col justify-center py-1">
                    {viewMode === 'list' ? (
                        <div style={{display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 32, rowGap: 8, fontSize: 14}}>
                            <span className="text-gray-500">
                                {t('splitOrder.template.estimatedParcels')}&nbsp;&nbsp;<span className="text-black font-bold text-lg">{forecastParcelCount ?? 0}{t('splitOrder.template.parcelsUnit')}</span>
                            </span>
                            <span className="text-gray-500">
                                {t('splitOrder.template.availableTemplates')}&nbsp;&nbsp;<span className="text-black font-bold text-lg">{templateList.length}{t('splitOrder.template.templatesUnit')}</span>
                            </span>
                            <span className="text-gray-500">
                                {t('splitOrder.template.currentDefaultTemplate')}&nbsp;&nbsp;<span className="text-black font-bold text-lg">{defaultTemplate?.name ?? '-'}</span>
                                {defaultTemplate && (
                                    <span className="text-red-500 ml-1">{t('splitOrder.template.autoRouteHint')}</span>
                                )}
                            </span>
                            <span className="text-gray-500">
                                {t('splitOrder.template.configuredDrivers')}&nbsp;&nbsp;<span className="text-black font-bold text-lg">{defaultTemplate?.driverCount ?? 0}{t('splitOrder.template.driversUnit')}</span>
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-8" style={{fontSize: 14}}>
                            <span className="text-gray-500">
                                {t('splitOrder.template.estimatedParcels')}&nbsp;&nbsp;<span className="text-black font-bold text-lg">{forecastParcelCount ?? 0}{t('splitOrder.template.parcelsUnit')}</span>
                            </span>
                            <span className="text-gray-500">
                                {t('splitOrder.template.configuredDrivers')}&nbsp;&nbsp;<span className="text-black font-bold text-lg">{segments.length}{t('splitOrder.template.driversUnit')}</span>
                            </span>
                            <div className="flex-1"/>
                        </div>
                    )}
                </div>
                <div className="flex items-center">
                    <Button
                        onClick={onClose}
                        style={{marginRight: 16}}
                    >
                        {t('splitOrder.template.back')}
                    </Button>
                </div>
            </div>

            {/* ===== 主体区域 ===== */}
            <div className="flex flex-1 overflow-hidden">

                {/* 左侧操作面板 */}
                <div className="shrink-0 border-r border-gray-200 overflow-y-auto flex flex-col bg-white"
                     style={{width: 420}}>

                    {viewMode === 'list' ? (
                        <div className="px-4 py-3">
                            <div ref={templateListRef}>
                            {templateList.map(tpl => (
                                <div
                                    key={tpl.id}
                                    className="rounded-xl border p-4 mb-3"
                                    style={tpl.isDefault
                                        ? {background: '#f5f0ff', borderColor: '#7c3aed', boxShadow: '0 1px 4px rgba(124,58,237,0.1)'}
                                        : {background: '#fff', borderColor: '#e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)'}
                                    }
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-base">{tpl.name}</span>
                                            {tpl.isDefault && (
                                                <span className="text-xs text-white rounded px-1.5 py-0.5" style={{background: '#7c3aed'}}>{t('splitOrder.template.defaultBadge')}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {isTemplateEditable && !tpl.isDefault && (<>
                                                <span className="text-xs text-gray-400">{t('splitOrder.template.setDefault')}</span>
                                                <Switch
                                                    size="small"
                                                    defaultChecked={false}
                                                    onChange={async () => {
                                                        try {
                                                            await setDefaultMutation({
                                                                variables: {id: tpl.id, areaId: record?.areaId ?? 0, warehouseId: warehouseId ?? 0},
                                                            });
                                                            notification.success({message: t('splitOrder.template.setDefaultSuccess'), duration: 2});
                                                            await refetchTemplates();
                                                        } catch (err: any) {
                                                            notification.error({message: translateGraphQLError(err, 'splitOrder.template.operationFailed'), duration: 3});
                                                        }
                                                    }}
                                                />
                                            </>)}
                                            <button
                                                className="hover:opacity-80" style={{color: '#7c3aed'}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingTemplateId(tpl.id);
                                                    setTemplateName(tpl.name);
                                                    setSegments(tpl.details.map((d, i) => ({
                                                        id: `seg-${tpl.id}-${i}`,
                                                            detailId: d.id ?? null,
                                                            name: `${record?.areaName ?? 'Area'}-${i + 1}`,
                                                            colorIndex: i % SEGMENT_ACCENT_COLORS.length,
                                                            driverAccountId: d.driverAccountId ? String(d.driverAccountId) : '',
                                                            driverName: d.driverName ?? '',
                                                            capacityMin: d.capacityMin ?? null,
                                                            capacityMax: d.capacityMax ?? null,
                                                            preferredZipcodes: d.preferredZipcodes ?? [],
                                                            startAddress: d.startAddress ?? null,
                                                            endAddress: d.endAddress ?? null,
                                                        })));
                                                        setViewMode('editing');
                                                    }}
                                                >
                                                    {isTemplateEditable ? <EditOutlined style={{fontSize: 16}}/> : <EyeOutlined style={{fontSize: 16}}/>}
                                                </button>
                                            {isTemplateEditable && !tpl.isDefault && (
                                                <button
                                                    className="text-red-400 hover:text-red-500"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        Modal.confirm({
                                                            title: t('splitOrder.template.deleteConfirmTitle'),
                                                            content: t('splitOrder.template.deleteConfirmContent', {name: tpl.name}),
                                                            okText: t('splitOrder.template.deleteConfirmOk'),
                                                            okButtonProps: {danger: true},
                                                            cancelText: t('splitOrder.template.cancel'),
                                                            onOk: async () => {
                                                                try {
                                                                    await deleteTemplateMutation({variables: {id: tpl.id}});
                                                                    notification.success({message: t('splitOrder.template.deleted'), duration: 2});
                                                                    await refetchTemplates();
                                                                } catch (err: any) {
                                                                    notification.error({message: translateGraphQLError(err, 'splitOrder.template.deleteFailed'), duration: 3});
                                                                }
                                                            },
                                                        });
                                                    }}
                                                >
                                                    <DeleteOutlined style={{fontSize: 16}}/>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-600 mb-1">
                                        <span className="text-gray-400 mr-2">{t('splitOrder.template.splitMode')}</span>
                                        <span className="font-medium">{getAssignTypeLabel(tpl.assignType)}</span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <span className="text-gray-400 mr-2">{t('splitOrder.template.driverCount')}</span>
                                        <span className="font-medium">{t('splitOrder.template.driverCountUnit', {count: tpl.driverCount})}</span>
                                    </div>
                                </div>
                            ))}
                            </div>
                            <div className="sticky bottom-0 z-10 py-2 pb-4 flex justify-center bg-white mt-1">
                                {isTemplateEditable && (
                                    <Button
                                        onClick={() => {
                                            setEditingTemplateId(null);
                                            setTemplateName('');
                                            setSegments([createEmptySegment(0, record?.areaName ?? 'Area')]);
                                            setViewMode('editing');
                                        }}
                                        type="primary"
                                        style={{
                                            backgroundColor: '#7c3aed',
                                            borderColor: '#7c3aed',
                                            paddingInline: 14,
                                            fontSize: 13,
                                        }}
                                    >
                                        {t('splitOrder.template.addTemplate')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* ---- 创建/编辑视图 ---- */
                        <div className="p-4 pb-0 flex flex-col gap-3">
                                <div className="mb-2">
                                    <Text className="text-lg mb-2 block font-bold">{t('splitOrder.template.templateNameLabel')}</Text>
                                    {isTemplateEditable ? (
                                        <Input
                                            value={templateName}
                                            onChange={e => setTemplateName(e.target.value)}
                                            placeholder={t('splitOrder.template.templateNamePlaceholder') ?? ''}
                                            size="large"
                                            maxLength={50}
                                        />
                                    ) : (
                                        <div className="rounded-lg border px-3 py-2 text-base font-medium text-gray-800"
                                            style={{borderColor: '#e5e7eb', background: '#f9fafb'}}>
                                            {templateName || '—'}
                                        </div>
                                    )}
                                </div>

                                <Text className="text-lg mb-2 block font-bold">{t('splitOrder.template.driverTemplate')}</Text>
                                {segments.map((seg) => {
                                    const accentColor = SEGMENT_ACCENT_COLORS[seg.colorIndex];

                                    // 不可编辑时段：整段做成只读展示卡片（参考 SplitRouteHistoryDrawer 的分段卡片），而非禁用输入框
                                    if (!isTemplateEditable) {
                                        const segDriver = (driversByDspId[record?.dspId ?? 0] ?? []).find(d => String(d.accountId) === seg.driverAccountId);
                                        const segDriverPhone = segDriver?.phone;
                                        return (
                                            <div
                                                key={seg.id}
                                                className="rounded-lg py-6 px-4 flex flex-col gap-2.5"
                                                style={{
                                                    border: `1px solid ${accentColor}4D`,
                                                    backgroundColor: `${accentColor}1D`,
                                                }}
                                            >
                                                <div className="flex items-center mb-2">
                                                    <span className="text-lg font-bold text-gray-800">{seg.name}</span>
                                                </div>
                                                <div className="flex flex-col gap-2 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-500 font-medium shrink-0 whitespace-nowrap">{t('splitOrder.template.selectDriver')}</span>
                                                        <span className="text-gray-800 font-medium">
                                                            {seg.driverName || '—'}
                                                            {segDriverPhone && <span className="text-gray-500 font-normal">（{segDriverPhone}）</span>}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-500 font-medium shrink-0 whitespace-nowrap">{t('splitOrder.template.driverCapacity')}</span>
                                                        <span className="text-gray-800">{seg.capacityMin != null && seg.capacityMax != null ? `${seg.capacityMin} ~ ${seg.capacityMax}` : '—'}</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-gray-500 font-medium shrink-0 whitespace-nowrap">{t('splitOrder.template.preferredArea')}</span>
                                                        <span className="text-gray-800">{seg.preferredZipcodes.length > 0 ? seg.preferredZipcodes.join(', ') : '—'}</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-gray-500 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.template.startPoint')}</span>
                                                        <span className="text-gray-700">{formatAddress(seg.startAddress) || '—'}</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-gray-500 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.template.endPoint')}</span>
                                                        <span className="text-gray-700">{formatAddress(seg.endAddress) || '—'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    const usedDriverIds = new Set(
                                        segments.filter(s => s.id !== seg.id && s.driverAccountId).map(s => s.driverAccountId)
                                    );
                                    const segDriverOptions = driverOptions.map(d => {
                                        const isUsedByOther = usedDriverIds.has(d.value);
                                        return {
                                            ...d,
                                            disabled: d.disabled || isUsedByOther,
                                            label: isUsedByOther ? (
                                                <span><span style={{fontSize: 12, color: '#999', marginRight: 8}}>{t('splitOrder.common.assigned')}</span>{d.label}</span>
                                            ) : d.label,
                                        };
                                    });
                                    return (

                                    <div
                                        key={seg.id}
                                        className="rounded-lg py-6 px-4 flex flex-col gap-2.5"
                                        style={{
                                            border: `1px solid ${accentColor}4D`,
                                            backgroundColor: `${accentColor}1D`,
                                        }}
                                    >
                                        {/* 标题行 */}
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-lg font-bold text-gray-800">{seg.name}</span>
                                            <button
                                                onClick={() => setSegments(prev => {
                                                    const an = record?.areaName ?? 'Area';
                                                    return prev
                                                        .filter(s => s.id !== seg.id)
                                                        .map((s, i) => ({
                                                            ...s,
                                                            name: `${an}-${i + 1}`,
                                                            colorIndex: i % SEGMENT_ACCENT_COLORS.length,
                                                        }));
                                                })}
                                                className="flex items-center gap-1 text-sm text-red-400 hover:text-red-600"
                                            >
                                                <DeleteOutlined/><span>{t('splitOrder.template.deleteSegment')}</span>
                                            </button>
                                        </div>

                                        {/* 选择派送司机 */}
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                <span className="text-red-500">*&nbsp;</span>{t('splitOrder.template.selectDriver')}
                                            </span>
                                            <Select
                                                showSearch
                                                placeholder={t('splitOrder.template.selectDriverPlaceholder')}
                                                value={seg.driverAccountId || undefined}
                                                onChange={val => {
                                                    const driver = driverOptions.find(d => d.value === val);
                                                    updateSegment(seg.id, {driverAccountId: val, driverName: driver?.name ?? ''});
                                                }}
                                                style={{flex: 1, minWidth: 0, height: 34}}
                                                popupMatchSelectWidth={false}
                                                dropdownStyle={{minWidth: 420}}
                                                options={segDriverOptions}
                                                filterOption={driverFilterOption}
                                            />
                                        </div>

                                        {/* 司机配送能力：失焦校验，不符合直接清空该输入框，并在下方红字提示 */}
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                <span className="text-red-500">*&nbsp;</span>{t('splitOrder.template.driverCapacity')}
                                            </span>
                                            <Input
                                                size="small"
                                                placeholder={t('splitOrder.template.capacityMin') ?? ''}
                                                value={seg.capacityMin != null ? String(seg.capacityMin) : ''}
                                                onChange={e => {
                                                    const v = e.target.value.replace(/\D/g, '').replace(/^0+/, '');
                                                    updateSegment(seg.id, {capacityMin: v ? Number(v) : null});
                                                    setCapacityErrors(prev => { const n = {...prev}; delete n[seg.id]; return n; });
                                                }}
                                                onBlur={() => {
                                                    // 下限须≥20，且必须小于上限，不符合直接清空下限
                                                    if (seg.capacityMin == null) return;
                                                    if (seg.capacityMin < 20) {
                                                        updateSegment(seg.id, {capacityMin: null});
                                                        setCapacityErrors(prev => ({...prev, [seg.id]: t('splitOrder.template.capacityMinBelow20')}));
                                                    } else if (seg.capacityMax != null && seg.capacityMin >= seg.capacityMax) {
                                                        updateSegment(seg.id, {capacityMin: null});
                                                        setCapacityErrors(prev => ({...prev, [seg.id]: t('splitOrder.template.capacityMinGteMax')}));
                                                    }
                                                }}
                                                status={capacityErrors[seg.id] ? 'error' : undefined}
                                                style={{height: 30}}
                                            />
                                            <span className="text-gray-400">~</span>
                                            <Input
                                                size="small"
                                                placeholder={t('splitOrder.template.capacityMax') ?? ''}
                                                value={seg.capacityMax != null ? String(seg.capacityMax) : ''}
                                                onChange={e => {
                                                    const v = e.target.value.replace(/\D/g, '').replace(/^0+/, '');
                                                    updateSegment(seg.id, {capacityMax: v ? Number(v) : null});
                                                    setCapacityErrors(prev => { const n = {...prev}; delete n[seg.id]; return n; });
                                                }}
                                                onBlur={() => {
                                                    // 上限必须大于下限，不符合直接清空上限
                                                    if (seg.capacityMax != null && seg.capacityMin != null && seg.capacityMax <= seg.capacityMin) {
                                                        updateSegment(seg.id, {capacityMax: null});
                                                        setCapacityErrors(prev => ({...prev, [seg.id]: t('splitOrder.template.capacityMaxLteMin')}));
                                                    }
                                                }}
                                                status={capacityErrors[seg.id] ? 'error' : undefined}
                                                style={{height: 30}}
                                            />
                                        </div>
                                        {capacityErrors[seg.id] && (
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm shrink-0 whitespace-nowrap" style={{visibility: 'hidden'}}>
                                                    <span>*&nbsp;</span>{t('splitOrder.template.driverCapacity')}
                                                </span>
                                                <span className="text-xs text-red-500">{capacityErrors[seg.id]}</span>
                                            </div>
                                        )}

                                        {/* 期望配送区域 */}
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                {t('splitOrder.template.preferredArea')}
                                            </span>
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                placeholder={t('splitOrder.template.selectZipcodePlaceholder')}
                                                value={seg.preferredZipcodes}
                                                onChange={(val: string[]) => updateSegment(seg.id, {preferredZipcodes: val})}
                                                style={{flex: 1, minWidth: 0}}
                                                options={zipcodeOptions}
                                                filterOption={(input, opt) => String(opt?.value ?? '').includes(input)}
                                            />
                                        </div>

                                        {/* 起点 */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.template.startPoint')}</span>
                                            <Input
                                                size="small"
                                                readOnly
                                                placeholder={t('splitOrder.template.clickInputAddress') ?? ''}
                                                value={formatAddress(seg.startAddress)}
                                                style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                onClick={() => setAddressModalTarget({segId: seg.id, field: 'start'})}
                                            />
                                            <Checkbox
                                                checked={isWhsAddress(seg.startAddress)}
                                                onChange={e => updateSegment(seg.id, {startAddress: e.target.checked ? whsDriverAddress : null})}
                                            >
                                                <span className="text-gray-700 font-medium">WHS</span>
                                            </Checkbox>
                                            <button className="flex items-center gap-0.5 shrink-0" style={{color: accentColor}}
                                                onClick={() => setPinningTarget({segId: seg.id, field: 'start'})}>
                                                <EnvironmentOutlined/><span>{t('splitOrder.template.mapPin')}</span>
                                            </button>
                                        </div>

                                        {/* 终点 */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.template.endPoint')}</span>
                                            <Input
                                                size="small"
                                                readOnly
                                                placeholder={t('splitOrder.template.clickInputAddress') ?? ''}
                                                value={formatAddress(seg.endAddress)}
                                                style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                onClick={() => setAddressModalTarget({segId: seg.id, field: 'end'})}
                                            />
                                            <Checkbox
                                                checked={isWhsAddress(seg.endAddress)}
                                                onChange={e => updateSegment(seg.id, {endAddress: e.target.checked ? whsDriverAddress : null})}
                                            >
                                                <span className="text-gray-700 font-medium">WHS</span>
                                            </Checkbox>
                                            <button className="flex items-center gap-0.5 shrink-0" style={{color: accentColor}}
                                                onClick={() => setPinningTarget({segId: seg.id, field: 'end'})}>
                                                <EnvironmentOutlined/><span>{t('splitOrder.template.mapPin')}</span>
                                            </button>
                                        </div>
                                    </div>
                                    );
                                })}

                            <div className="sticky bottom-0 z-10 py-2 pb-4 flex justify-around bg-white mt-1"
                                 style={{fontSize: 13}}>
                                <Button onClick={() => { setSegments([]); setTemplateName(''); setViewMode('list'); }}
                                    style={{
                                    color: '#7c3aed',
                                    borderColor: '#7c3aed',
                                    backgroundColor: '#fff',
                                    borderWidth: 1.5,
                                    paddingInline: 14,
                                    fontSize: 13,
                                }}>
                                    {isTemplateEditable ? t('splitOrder.template.cancel') : t('splitOrder.template.back')}
                                </Button>
                                {isTemplateEditable && (
                                    <Button
                                        onClick={() => setSegments(prev => [
                                            ...prev,
                                            createEmptySegment(prev.length, record?.areaName ?? 'Area'),
                                        ])}
                                        style={{
                                            color: '#7c3aed',
                                            borderColor: '#7c3aed',
                                            backgroundColor: '#fff',
                                            borderWidth: 1.5,
                                            paddingInline: 14,
                                            fontSize: 13,
                                        }}
                                    >
                                        {t('splitOrder.template.addDriver')}
                                    </Button>
                                )}
                                {isTemplateEditable && (
                                    <Button
                                        type="primary"
                                        loading={saving}
                                        onClick={handleSaveTemplate}
                                        style={{
                                            backgroundColor: '#7c3aed',
                                            borderColor: '#7c3aed',
                                            paddingInline: 14,
                                            fontSize: 13,
                                        }}
                                    >
                                        {t('splitOrder.template.saveTemplate')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* 右侧地图区域 */}
                <div className="flex-1 min-w-0 flex flex-col">
                    {editStartTime && editEndTime && (
                        <div className="px-4 py-2.5 flex items-center gap-2" style={{backgroundColor: '#FFF1F0'}}>
                            <ExclamationCircleOutlined style={{color: '#ff4d4f', fontSize: 16}} />
                            <span className="text-red-500 font-bold" style={{fontSize: 14}}>
                                {t('splitOrder.template.editWindowWarning', {start: editStartTime, end: editEndTime})}
                            </span>
                        </div>
                    )}
                    <div className="flex-1 min-h-0 relative">
                        {isLoaded ? (
                            <>
                            <GoogleMap
                                mapContainerStyle={{width: '100%', height: '100%'}}
                                center={mapCenter}
                                zoom={13}
                                clickableIcons={false}
                                onClick={handleMapClick}
                                onLoad={map => { mapRef.current = map; }}
                                options={{
                                    disableDefaultUI: true,
                                    disableDoubleClickZoom: true,
                                    draggableCursor: pinningTarget ? 'crosshair' : undefined,
                                    styles: hideMapLabels ? [
                                        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'landscape', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                    ] : [],
                                }}
                            >
                                {showZipcodeBoundary && zipcodeBoundaryData.map(entry => (
                                    <Polygon
                                        key={`zb-${entry.zipcode}`}
                                        paths={entry.paths}
                                        options={{
                                            fillColor: '#7c3aed',
                                            fillOpacity: 0.12,
                                            strokeColor: '#7c3aed',
                                            strokeOpacity: 0.6,
                                            strokeWeight: 1.5,
                                            zIndex: 1,
                                            clickable: false,
                                        }}
                                    />
                                ))}

                                {showAddress && segments.map(seg => {
                                    const color = SEGMENT_ACCENT_COLORS[seg.colorIndex];
                                    const startLat = seg.startAddress?.latitude;
                                    const startLng = seg.startAddress?.longitude;
                                    const endLat = seg.endAddress?.latitude;
                                    const endLng = seg.endAddress?.longitude;
                                    return (
                                        <React.Fragment key={`addr-${seg.id}`}>
                                            {startLat != null && startLng != null && (
                                                <Marker
                                                    position={{lat: startLat, lng: startLng}}
                                                    icon={{
                                                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                                                            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32"><path d="M12 0C6 0 1 5 1 11c0 8 11 21 11 21s11-13 11-21C23 5 18 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/></svg>`
                                                        ),
                                                        scaledSize: new google.maps.Size(24, 32),
                                                        anchor: new google.maps.Point(12, 32),
                                                    }}
                                                    clickable={false}
                                                />
                                            )}
                                            {endLat != null && endLng != null && (
                                                <Marker
                                                    position={{lat: endLat, lng: endLng}}
                                                    icon={{
                                                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                                                            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32"><path d="M12 0C6 0 1 5 1 11c0 8 11 21 11 21s11-13 11-21C23 5 18 0 12 0z" fill="white" stroke="${color}" stroke-width="2"/></svg>`
                                                        ),
                                                        scaledSize: new google.maps.Size(24, 32),
                                                        anchor: new google.maps.Point(12, 32),
                                                    }}
                                                    clickable={false}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </GoogleMap>

                            {/* 方向 + 缩放控件 */}
                            <div className="absolute top-3 right-3 z-10 flex flex-col items-center gap-1.5 pointer-events-auto">
                                <div className="flex flex-col items-center">
                                    {panExpanded && (
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 mb-0.5"
                                            onClick={() => mapRef.current?.panBy(0, -80)}
                                        >▲</button>
                                    )}
                                    <div className="flex items-center gap-0.5">
                                        {panExpanded && (
                                            <button
                                                className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                onClick={() => mapRef.current?.panBy(-80, 0)}
                                            >◀</button>
                                        )}
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-500 hover:bg-gray-50"
                                            title={t('splitOrder.template.directionControl') ?? ''}
                                            onClick={() => setPanExpanded(v => !v)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                <polygon points="8,1 10,5 8,4 6,5"/>
                                                <polygon points="8,15 10,11 8,12 6,11"/>
                                                <polygon points="1,8 5,6 4,8 5,10"/>
                                                <polygon points="15,8 11,6 12,8 11,10"/>
                                                <circle cx="8" cy="8" r="1.5"/>
                                            </svg>
                                        </button>
                                        {panExpanded && (
                                            <button
                                                className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                onClick={() => mapRef.current?.panBy(80, 0)}
                                            >▶</button>
                                        )}
                                    </div>
                                    {panExpanded && (
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 mt-0.5"
                                            onClick={() => mapRef.current?.panBy(0, 80)}
                                        >▼</button>
                                    )}
                                </div>
                                <button
                                    className="w-8 h-8 bg-white rounded-t shadow flex items-center justify-center text-lg text-gray-600 hover:bg-gray-50 leading-none"
                                    onClick={() => mapRef.current && mapRef.current.setZoom((mapRef.current.getZoom() ?? 13) + 1)}
                                >+</button>
                                <button
                                    className="w-8 h-8 bg-white rounded-b shadow flex items-center justify-center text-lg text-gray-600 hover:bg-gray-50 leading-none -mt-px"
                                    onClick={() => mapRef.current && mapRef.current.setZoom((mapRef.current.getZoom() ?? 13) - 1)}
                                >−</button>
                            </div>

                            {/* 地图图层控制面板 */}
                            <div
                                className="absolute bottom-6 left-4 z-10 bg-white rounded-xl shadow-lg px-4 py-3 flex flex-col gap-2 min-w-[160px]"
                                style={{border: '1px solid #e5e7eb'}}>
                                {[
                                    {
                                        label: t('splitOrder.template.zipcodeBoundary'),
                                        icon: <span style={{
                                            display: 'inline-block',
                                            width: 14,
                                            height: 14,
                                            background: '#7c3aed',
                                            borderRadius: 3,
                                            opacity: 0.5,
                                        }}/>,
                                        value: showZipcodeBoundary,
                                        setter: setShowZipcodeBoundary,
                                    },
                                    {
                                        label: t('splitOrder.template.hideMapLabels'),
                                        icon: <span style={{fontSize: 14, color: '#6b7280'}}>🏷</span>,
                                        value: hideMapLabels,
                                        setter: setHideMapLabels,
                                    },
                                    {
                                        label: t('splitOrder.template.address'),
                                        icon: <EnvironmentOutlined style={{fontSize: 14, color: '#6b7280'}}/>,
                                        value: showAddress,
                                        setter: setShowAddress,
                                    },
                                ].map(({label, icon, value, setter}) => (
                                    <div key={label} className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="flex items-center justify-center w-4 h-4">{icon}</span>
                                            <span className="text-xs text-gray-700 whitespace-nowrap">{label}</span>
                                        </div>
                                        <button
                                            onClick={() => setter((v: boolean) => !v)}
                                            className="relative flex-shrink-0"
                                            style={{width: 36, height: 20}}
                                        >
                                            <span style={{
                                                display: 'block', width: 36, height: 20, borderRadius: 10,
                                                background: value ? '#7c3aed' : '#d1d5db',
                                                transition: 'background 0.2s',
                                            }}/>
                                            <span style={{
                                                position: 'absolute', top: 3, left: value ? 19 : 3,
                                                width: 14, height: 14, borderRadius: '50%',
                                                background: 'white', transition: 'left 0.2s',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }}/>
                                        </button>
                                    </div>
                                ))}
                                <div style={{borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 6}}
                                     className="flex items-center gap-4">
                                    <div className="flex items-center gap-1">
                                        <svg width="10" height="14" viewBox="0 0 24 32">
                                            <path d="M12 0C6 0 1 5 1 11c0 8 11 21 11 21s11-13 11-21C23 5 18 0 12 0z"
                                                  fill="#6b7280" stroke="white" strokeWidth="1.5"/>
                                        </svg>
                                        <span className="text-xs text-gray-500">{t('splitOrder.template.legendStart')}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <svg width="10" height="14" viewBox="0 0 24 32">
                                            <path d="M12 0C6 0 1 5 1 11c0 8 11 21 11 21s11-13 11-21C23 5 18 0 12 0z"
                                                  fill="white" stroke="#6b7280" strokeWidth="2"/>
                                        </svg>
                                        <span className="text-xs text-gray-500">{t('splitOrder.template.legendEnd')}</span>
                                    </div>
                                </div>
                            </div>

                            {pinningTarget && (
                                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-black/75 text-white px-4 py-2 rounded-lg text-sm">
                                    <span>{pinningTarget.field === 'start' ? t('splitOrder.template.pinStartHint') : t('splitOrder.template.pinEndHint')}</span>
                                    <button className="ml-3 underline" onClick={() => setPinningTarget(null)}>{t('splitOrder.template.pinCancel')}</button>
                                </div>
                            )}
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-400">
                                {t('splitOrder.template.mapLoading')}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AddressInputModal
                open={!!addressModalTarget}
                onConfirm={handleAddressConfirm}
                onCancel={() => setAddressModalTarget(null)}
            />

            {/* 保存时配送能力软校验弹窗：命中场景后展示所有提示，允许"仍要提交"或"返回编辑" */}
            <Modal
                open={!!capacityWarnings}
                closable={false}
                maskClosable={false}
                title={<div className="flex items-center gap-2"><ExclamationCircleOutlined style={{color: '#faad14'}} />{t('splitOrder.template.capacityUnreasonable')}</div>}
                footer={
                    <div className="flex justify-end gap-3">
                        <Button onClick={() => { setCapacityWarnings(null); doSaveTemplate(); }}>
                            {t('splitOrder.template.submitAnyway')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => setCapacityWarnings(null)}
                            style={{backgroundColor: '#7c3aed', borderColor: '#7c3aed'}}
                        >
                            {t('splitOrder.template.backToEdit')}
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-2">
                    {(capacityWarnings ?? []).map((w, i) => (
                        <div key={i} className="text-sm text-gray-700">{w}</div>
                    ))}
                </div>
            </Modal>
        </Drawer>
    );
};

export default RoutePlanningTemplate;